import React, { useMemo } from 'react';
import { motion } from 'framer-motion';
import { Search } from 'lucide-react';
import Select, { components } from 'react-select';
import { useTranslation } from 'react-i18next';

import { getReportTypes } from './reportTypeConfig';

// Full-size widget imports
import CreatedVsResolvedWidget from './CreatedVsResolvedWidget';
import WorkOrdersByTypeWidget from './WorkOrdersByTypeWidget';
import RepeatingReportWidget from './RepeatingReportWidget';
import StatusReportWidget from './StatusReportWidget';
import PriorityReportWidget from './PriorityReportWidget';
import TimeToCompleteWidget from './TimeToCompleteWidget';
import OnTimeOverdueWidget from './OnTimeOverdueWidget';
import CompletedByUserWidget from './CompletedByUserWidget';
import StatusDurationWidget from './StatusDurationWidget';
import TotalDowntimeWidget from './TotalDowntimeWidget';
import MtbfWidget from './MtbfWidget';
import WorkOrderSpendWidget from './WorkOrderSpendWidget';
import WorkOrderCostByWoWidget from './WorkOrderCostByWoWidget';

const DropdownIndicator = (props) => (
  <components.DropdownIndicator {...props}>
    <Search size={16} className="text-muted" />
  </components.DropdownIndicator>
);

/**
 * ReportingDetailsTab - Detail view for a single report type.
 *
 * Shows the selected report's full widget with a dropdown to switch between types.
 *
 * Entry point: user clicks a widget on Tab 1/2, which sets selectedReport and switches to Tab 3.
 */
const ReportingDetailsTab = ({
  selectedReport = 'createdVsResolved',
  onReportChange,
  organizationId,
  dateRange,
  filters = {},
  reportData = {},
  reportLoading = {},
  reportErrors = {},
  handlers = {},
  // Optional whitelist of report keys to show in the dropdown. The Global
  // Dashboard uses this to hide reports it doesn't fetch on the WO tab
  // (statusDurationReport / totalDowntime / mtbf — Asset-side concepts).
  // Per-org callers omit this prop and see all 11 reports as before.
  allowedReportKeys,
}) => {
  const { t } = useTranslation();

  const reportTypes = useMemo(() => {
    const all = getReportTypes(t);
    if (!Array.isArray(allowedReportKeys) || allowedReportKeys.length === 0) {
      return all;
    }
    const allow = new Set(allowedReportKeys);
    return all.filter(rt => allow.has(rt.key));
  }, [t, allowedReportKeys]);

  const dropdownOptions = useMemo(() => reportTypes.map(rt => ({
    value: rt.key,
    label: rt.title,
  })), [reportTypes]);

  const selectedOption = useMemo(
    () => dropdownOptions.find(o => o.value === selectedReport) || dropdownOptions[0],
    [selectedReport, dropdownOptions]
  );

  const renderFullWidget = (key) => {
    switch (key) {
      case 'createdVsResolved':
        return (
          <CreatedVsResolvedWidget
            data={reportData.createdVsResolved}
            loading={reportLoading.createdVsResolved}
            error={reportErrors.createdVsResolved}
            onCreatedClick={handlers.onCreatedClick}
            onResolvedClick={handlers.onResolvedClick}
            onChartPointClick={handlers.onChartPointClick}
          />
        );
      case 'workOrdersByType':
        return (
          <WorkOrdersByTypeWidget
            data={reportData.workOrdersByType}
            loading={reportLoading.workOrdersByType}
            error={reportErrors.workOrdersByType}
            onPreventiveClick={handlers.onPreventiveClick}
            onReactiveClick={handlers.onReactiveClick}
            onOtherClick={handlers.onOtherClick}
            onChartBarClick={handlers.onWorkTypeChartBarClick}
          />
        );
      case 'repeatingReport':
        return (
          <RepeatingReportWidget
            data={reportData.repeatingReport}
            loading={reportLoading.repeatingReport}
            error={reportErrors.repeatingReport}
            onNonRepeatingClick={handlers.onNonRepeatingClick}
            onRepeatingClick={handlers.onRepeatingClick}
            onChartBarClick={handlers.onRepeatingChartBarClick}
          />
        );
      case 'statusReport':
        return (
          <StatusReportWidget
            data={reportData.statusReport}
            loading={reportLoading.statusReport}
            error={reportErrors.statusReport}
            onStatusClick={handlers.onStatusClick}
          />
        );
      case 'priorityReport':
        return (
          <PriorityReportWidget
            data={reportData.priorityReport}
            loading={reportLoading.priorityReport}
            error={reportErrors.priorityReport}
            onPriorityClick={handlers.onPriorityClick}
          />
        );
      case 'timeToComplete':
        return (
          <TimeToCompleteWidget
            data={reportData.timeToComplete}
            loading={reportLoading.timeToComplete}
            error={reportErrors.timeToComplete}
            onTotalHoursClick={handlers.onTotalHoursClick}
            onAvgHoursClick={handlers.onAvgHoursClick}
            onMttrClick={handlers.onMttrClick}
            onChartPointClick={handlers.onTimeToCompleteChartClick}
          />
        );
      case 'onTimeOverdue':
        return (
          <OnTimeOverdueWidget
            data={reportData.onTimeOverdue}
            loading={reportLoading.onTimeOverdue}
            error={reportErrors.onTimeOverdue}
            onOnTimeClick={handlers.onOnTimeClick}
            onOverdueClick={handlers.onOverdueClick}
            onSegmentClick={handlers.onOnTimeOverdueSegmentClick}
          />
        );
      case 'completedByUser':
        return (
          <CompletedByUserWidget
            data={reportData.completedByUser}
            loading={reportLoading.completedByUser}
            error={reportErrors.completedByUser}
            onUserWeekClick={handlers.onUserWeekClick}
          />
        );
      case 'statusDurationReport':
        return (
          <StatusDurationWidget
            data={reportData.statusDurationReport}
            loading={reportLoading.statusDurationReport}
            error={reportErrors.statusDurationReport}
          />
        );
      case 'totalDowntime':
        return (
          <TotalDowntimeWidget
            organizationId={organizationId}
            dateRange={dateRange}
            criticality={filters.criticality}
            locationId={filters.locationId}
            vendorId={filters.vendorId}
            assetId={filters.assetId}
            assetTypeId={filters.assetTypeId}
          />
        );
      case 'mtbf':
        return (
          <MtbfWidget
            organizationId={organizationId}
            dateRange={dateRange}
            criticality={filters.criticality}
            locationId={filters.locationId}
            vendorId={filters.vendorId}
            assetId={filters.assetId}
            assetTypeId={filters.assetTypeId}
            contractNumber={filters.contractNumber}
            workOrderCategoryId={filters.workOrderCategoryId}
          />
        );
      case 'workOrderCost':
        return (
          <div className="d-flex flex-column gap-4">
            <WorkOrderSpendWidget
              data={reportData.workOrderCost}
              loading={reportLoading.workOrderCost}
              error={reportErrors.workOrderCost}
            />
            <WorkOrderCostByWoWidget
              data={reportData.workOrderCost}
              loading={reportLoading.workOrderCost}
              error={reportErrors.workOrderCost}
              onRowClick={handlers.onCostRowClick}
            />
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div>
      {/* Header: Dropdown */}
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        className="d-flex justify-content-end mb-4"
      >
        <div style={{ minWidth: 280 }}>
          <Select
            options={dropdownOptions}
            value={selectedOption}
            onChange={(opt) => opt && onReportChange?.(opt.value)}
            isSearchable
            components={{ DropdownIndicator }}
            placeholder={t('shared.search_reports', 'Search reports...')}
            styles={{
              control: (base) => ({
                ...base,
                borderRadius: '10px',
                borderColor: '#dee2e6',
                minHeight: '40px',
                boxShadow: 'none',
                '&:hover': { borderColor: '#adb5bd' },
              }),
              option: (base, state) => ({
                ...base,
                backgroundColor: state.isSelected ? '#3980c0' : state.isFocused ? '#e8f0fe' : '#fff',
                color: state.isSelected ? '#fff' : '#333',
              }),
            }}
          />
        </div>
      </motion.div>

      {/* Full widget */}
      <div className="mb-4">
        {renderFullWidget(selectedReport)}
      </div>
    </div>
  );
};

export default ReportingDetailsTab;
